import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { ListFooter } from '@/components/ListPager';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { apiFormData, listResource } from '@/lib/api';

export interface MediaPickerValue {
  id: string;
  title: string;
  mimeType?: string;
  size?: string;
}

interface MediaPickerProps {
  label: string;
  value: MediaPickerValue | null;
  onChange: (value: MediaPickerValue | null) => void;
  filterType?: string | null;
}

interface MediaFileRow {
  id: string;
  title?: string;
  filename?: string;
  mimeType?: string;
  size?: string;
}

function matchesFilter(file: MediaFileRow, filterType?: string | null) {
  if (!filterType) return true;
  const mime = file.mimeType ?? '';
  if (filterType === 'video') return mime.startsWith('video/');
  if (filterType === 'audio') return mime.startsWith('audio/');
  if (filterType === 'image') return mime.startsWith('image/');
  if (filterType === 'pdf') return mime.includes('pdf');
  return true;
}

function displayName(file: MediaFileRow) {
  return file.title ?? file.filename ?? 'Archivo';
}

export function MediaPicker({ label, value, onChange, filterType }: MediaPickerProps) {
  const queryClient = useQueryClient();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const appliedSearch = useDebouncedValue(search, 300).trim();

  useEffect(() => {
    setPage(1);
  }, [appliedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['media', 'picker', appliedSearch, page, filterType ?? ''],
    queryFn: () =>
      listResource<MediaFileRow>('/media', {
        page,
        perPage: 30,
        ...(appliedSearch ? { search: appliedSearch } : {}),
        ...(filterType ? { type: filterType } : {}),
      }),
    enabled: open,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const isPdf = file.type.includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
      if (filterType === 'pdf' && !isPdf) {
        throw new Error('Solo se pueden agregar archivos PDF.');
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);
      return apiFormData<MediaFileRow>('/media/upload', formData);
    },
    onSuccess: async (file) => {
      setUploadError(null);
      await queryClient.invalidateQueries({ queryKey: ['media'] });
      onChange({ id: file.id, title: displayName(file), mimeType: file.mimeType, size: file.size });
      setOpen(false);
    },
    onError: (error: Error) => setUploadError(error.message),
  });

  const files = (data?.data ?? []).filter((file) => matchesFilter(file, filterType));
  const total = data?.meta?.total ?? files.length;
  const perPage = data?.meta?.perPage ?? 30;
  const totalPages = data?.meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-surface-700">{label}</label>

      <div className="flex items-center gap-3 rounded-lg border border-surface-200 bg-surface-50 p-3">
        {value ? (
          <div className="flex flex-1 items-center gap-3">
            <div>
              <p className="text-sm font-medium">{value.title}</p>
              {value.mimeType && (
                <p className="text-xs text-surface-500">
                  {value.mimeType}
                  {value.size ? ` · ${value.size}` : ''}
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="flex-1 text-sm text-surface-500">
            Selecciona un archivo de la Biblioteca Multimedia o súbelo aquí
          </p>
        )}

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <input
            ref={uploadRef}
            type="file"
            accept={filterType === 'pdf' ? 'application/pdf,.pdf' : undefined}
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadMutation.mutate(file);
              event.target.value = '';
            }}
          />
          <button
            type="button"
            disabled={uploadMutation.isPending}
            className="rounded-lg border border-brand-600 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
            onClick={() => uploadRef.current?.click()}
          >
            {uploadMutation.isPending ? 'Subiendo…' : 'Subir archivo'}
          </button>
          <button
            type="button"
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            onClick={() => setOpen(true)}
          >
            Elegir archivo
          </button>
          {value && (
            <button type="button" className="text-sm text-surface-500 hover:text-danger-600" onClick={() => onChange(null)}>
              Quitar
            </button>
          )}
        </div>
      </div>
      {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-surface-950/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b p-4">
              <h3 className="font-semibold">Biblioteca Multimedia</h3>
              <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar">
                ✕
              </button>
            </div>
            <div className="border-b p-4">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Buscar archivo…"
                aria-label="Buscar archivo"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader label="Cargando archivos…" />
                </div>
              ) : files.length === 0 ? (
                appliedSearch ? (
                  <SearchEmptyState term={appliedSearch} onClear={() => setSearch('')} />
                ) : (
                  <p className="py-8 text-center text-sm text-surface-500">No hay archivos disponibles.</p>
                )
              ) : (
                <ul className="space-y-2">
                  {files.map((file) => (
                    <li key={file.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between rounded-lg border border-surface-200 px-4 py-3 text-left hover:bg-surface-50"
                        onClick={() => {
                          onChange({
                            id: file.id,
                            title: displayName(file),
                            mimeType: file.mimeType,
                            size: file.size,
                          });
                          setOpen(false);
                        }}
                      >
                        <span className="font-medium">{displayName(file)}</span>
                        <span className="text-xs text-surface-500">{file.mimeType}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <ListFooter
              page={page}
              totalPages={totalPages}
              total={total}
              start={rangeStart}
              end={rangeEnd}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
